import { useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface MultiSelectOption<V extends string | number = number> {
  value: V;
  label: string;
  /** Texto pequeño a la derecha: una fecha, un recuento. */
  hint?: string;
}

interface MultiSelectProps<V extends string | number> {
  options: ReadonlyArray<MultiSelectOption<V>>;
  value: ReadonlyArray<V>;
  onChange: (next: V[]) => void;
  /** Lo que dice el control sin nada elegido («Todos los eventos (12)»). */
  placeholder: string;
  /** Rótulo del campo de búsqueda; sin él no hay búsqueda. */
  searchPlaceholder?: string;
  /** Con varios elegidos: «{n} elegidos». */
  summary?: (n: number) => string;
  clearLabel?: string;
  selectAllLabel?: string;
  emptyLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  id?: string;
}

/**
 * Un `<select multiple>` que se puede usar: el control ocupa lo que un select
 * normal y despliega una lista con casillas, búsqueda y «todos / ninguno».
 * Vacío significa «sin acotar», que es lo que un filtro espera.
 */
export function MultiSelect<V extends string | number>({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  summary,
  clearLabel,
  selectAllLabel,
  emptyLabel,
  disabled,
  invalid,
  className,
  id,
}: MultiSelectProps<V>) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = useMemo(() => new Set<V>(value), [value]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(needle) || (o.hint ?? '').toLowerCase().includes(needle),
    );
  }, [options, q]);

  const toggle = (v: V) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    // Se conserva el orden del catálogo, no el del clic: la clave de caché y
    // el texto del control no deben cambiar según por dónde se empezó.
    onChange(options.filter((o) => next.has(o.value)).map((o) => o.value));
  };

  const label =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? String(value[0]))
        : (summary?.(value.length) ?? `${value.length}`);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQ('');
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={cn(
            'flex h-10 w-full items-center gap-2 rounded-lg border bg-[var(--color-bg-input)] px-3 text-left text-sm text-[var(--color-fg-primary)] transition-colors',
            'border-[var(--color-border-default)] hover:border-[var(--color-border-strong)] focus:border-[var(--color-accent-ink)] focus:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-60',
            invalid && 'border-[var(--color-error)]',
            className,
          )}
        >
          <span className={cn('min-w-0 flex-1 truncate', value.length === 0 && 'text-[var(--color-fg-muted)]')}>
            {label}
          </span>
          {value.length > 0 && (
            <span className="rounded-full bg-[var(--color-accent-10)] px-1.5 text-[10px] font-extrabold tabular-nums text-[var(--color-accent-ink)]">
              {value.length}
            </span>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 text-[var(--color-fg-subtle)]" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[260px] rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-2 shadow-xl"
        >
          {searchPlaceholder && (
            <div className="relative mb-2">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-8 w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-input)] pl-7 pr-7 text-xs text-[var(--color-fg-primary)] focus:border-[var(--color-accent-ink)] focus:outline-none"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-[var(--color-fg-subtle)] hover:text-[var(--color-fg-primary)]"
                  aria-label={clearLabel ?? 'clear'}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          )}
          <div className="mb-1 flex items-center justify-between px-1 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-fg-subtle)]">
            {selectAllLabel && (
              <button
                type="button"
                className="hover:text-[var(--color-accent-ink)]"
                onClick={() => onChange(shown.map((o) => o.value))}
              >
                {selectAllLabel}
              </button>
            )}
            {clearLabel && (
              <button
                type="button"
                className="hover:text-[var(--color-accent-ink)] disabled:opacity-40"
                disabled={value.length === 0}
                onClick={() => onChange([])}
              >
                {clearLabel}
              </button>
            )}
          </div>
          <ul role="listbox" aria-multiselectable className="max-h-64 overflow-y-auto">
            {shown.length === 0 && (
              <li className="px-2 py-3 text-center text-xs text-[var(--color-fg-subtle)]">{emptyLabel ?? '—'}</li>
            )}
            {shown.map((o) => {
              const on = selected.has(o.value);
              return (
                <li key={String(o.value)}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={on}
                    onClick={() => toggle(o.value)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-[var(--color-bg-elevated)]',
                      on && 'bg-[var(--color-accent-06)]',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        on
                          ? 'border-[var(--color-accent-ink)] bg-[var(--color-accent-ink)] text-[var(--color-fg-on-accent)]'
                          : 'border-[var(--color-border-strong)]',
                      )}
                    >
                      {on && <Check className="h-3 w-3" />}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[var(--color-fg-primary)]">{o.label}</span>
                    {o.hint && (
                      <span className="shrink-0 text-[11px] tabular-nums text-[var(--color-fg-subtle)]">{o.hint}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
